import * as THREE from 'three';

import * as et from "../../../../application/EventTypes";
import { FacetTypes } from '../../../facets/Facets';
import { HUD_LAYER, HudLayer } from '../HudLayer';
import { LevelLabel } from './LevelLabel';
import { LevelLabelPlacer } from './LevelPlacer';
import debounce from "../../../streams/timed-debounce";
import { flyToTopOfBox } from "../../../../tools/FitToViewUtil";
import { Label3D } from "../../../../gui/Label3D";
import { sortedArrayHas } from '../../../facets/sortedArray';

/** Level labels, seen when looking from the side, and shifted away from the actual level. */
export class LevelsLayer extends HudLayer {

  /** @type {Map<string, LevelLabel>} Label by level facet ID (its common name). */
  labelById = new Map();

  /** @type {Map<string, { left, right }>} Label by level facet ID (its common name). */
  placementById = new Map();

  /** @type {Array} List of filtered level facets */
  filteredLevels = [];

  #updatingLevelsFacet = false;
  #hasSelected = false;

  constructor(facility, hud, viewer) {
    super(HUD_LAYER.LEVELS.id, facility, hud, viewer);

    this.placer = new LevelLabelPlacer(this, () => this.#invalidatePlacement(true));
    this.boundInvalidateHulls = () => this.placer.invalidateHulls();
    this.boundOnGeomLoaded = this.#onGeomLoaded.bind(this);
    this.debouncedInvalidatePlacement = debounce(this.#invalidatePlacement.bind(this), 67 /* 4 frames at 60fps */);
  }

  clear() {
    this.labelById.forEach((l) => l.dispose());
    this.labelById.clear();
    this.placementById.clear();
    this.filteredLevels = [];
  }

  dispose() {
    this.clear();
    this.viewer.removeEventListener(et.GEOMETRY_LOADED_EVENT, this.boundOnGeomLoaded);
    this.viewer.removeEventListener(et.MODEL_UNLOADED_EVENT, this.boundInvalidateHulls);
    this.debouncedInvalidatePlacement.cancel();
  }

  init() {
    this.viewer.addEventListener(et.GEOMETRY_LOADED_EVENT, this.boundOnGeomLoaded);
    this.viewer.addEventListener(et.MODEL_UNLOADED_EVENT, this.boundInvalidateHulls);
    return this;
  }

  /** Level label placement is derived from the camera's perspective. */
  onCameraChanged( /* camera */) {
    // This is not using the debounced update as it uses the cached version.
    this.#invalidatePlacement(true);
  }

  onFacetsUpdated(event) {

    if (this.#updatingLevelsFacet) {
      return;
    }

    if (event.addedModel) {
      this.#invalidateFilteredLevels();
    }

    const levelsFacetDef = this.facility.facetsManager.getFacetDef(FacetTypes.levels);
    if (levelsFacetDef) {
      const hasFilter = levelsFacetDef.filter.size > 0;
      this.#hasSelected = hasFilter;
      for (const label of this.labelById.values()) {
        label.setSelected(levelsFacetDef.filter.has(label.level.id), hasFilter);
      }
    } else {
      this.#invalidateFilteredLevels();
      this.#hasSelected = false;
    }

  }

  onFacetsLoaded(event) {
    this.#invalidateFilteredLevels();
  }

  onIsolationChanged( /* isolation */) {/*this.#invalidateFilteredLevels();*/}

  onHiddenChanged( /* hidden */) {/*this.#invalidateFilteredLevels();*/}

  setVisible(visible) {

    super.setVisible(visible);

    if (visible) {
      // If we were off, we don't know about our latest placement.
      this.#invalidatePlacement();
    } else {
      this.invalidate();
    }

    this.facility.facetsManager.facetsEffects?.setFloorBoundaryVizEnabled(visible);
    this.facility.facetsManager.highlightLocations();
    this.viewer.impl.invalidate(true);
  }

  onClusterTransition(clustered) {
    if (clustered) {
      // Don't re-enable levels layer when clustered
      return false;
    }
  }

  onModelChanged({ change }) {
    const { modelId, dbIds } = change;
    if (!modelId || !dbIds) return;

    const model = this.facility.getModelByUrn(modelId);

    for (const facetItem of this.filteredLevels) {
      const ids = facetItem.ids.get(model);
      if (ids) {
        const found = dbIds.some((id) => sortedArrayHas(ids, id));
        if (found) {
          this.#invalidateFilteredLevels();
          return;
        }
      }
    }
  }

  onSelect(event, label) {
    const ext = this.viewer.getExtension('Autodesk.Edit2D');
    if (ext?.isCreatingModeOn(this.viewer.toolController.getActiveToolName())) {
      return;
    }

    const facetDefs = this.facility.facetsManager.getFacetDefs();
    const levelFacetIdx = this.facility.facetsManager.getFacetDefIndex(FacetTypes.levels);
    this.#hasSelected = true; //must be true given onSelect is being called

    if (!label.isSelected) {

      this.#updatingLevelsFacet = true;

      if (!event.shiftKey) {
        this.facility.facetsManager.clearFilter(FacetTypes.levels, true);
      }

      this.facility.facetsManager.setVisibility(levelFacetIdx, label.level.id, true);

      for (const label of this.labelById.values()) {
        label.setSelected(facetDefs[levelFacetIdx].filter.has(label.level.id), this.#hasSelected);
      }

      this.#invalidateFilteredLevels();

      this.#updatingLevelsFacet = false;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  onMouseOver(event, level) {
    if (!event.buttons) {
      this.facility.facetsManager.facetsEffects.addFloorHighlight(level);
    }

    event.preventDefault();
    event.stopPropagation();
  }

  onMouseOut(event) {
    this.facility.facetsManager.facetsEffects.clearFacetHighlight();

    event.preventDefault();
    event.stopPropagation();
  }

  onDblClick(event, level) {
    const bounds = getLevelBounds(this.facility, level);
    flyToTopOfBox(this.facility.viewer, bounds, 1.0);

    event.preventDefault();
    event.stopPropagation();
  }

  update(ts) {
    // Invalidated filtered levels not ready, skip this update but keep layer dirty
    if (this.filteredLevels === undefined) return true;

    let hasChanged = false;
    const unseen = new Set(this.labelById.keys());

    const isLayerVisible = this.isVisible();

    this.placer.reset();
    for (const level of this.filteredLevels) {
      unseen.delete(level.id);

      if (!isLayerVisible) {
        hasChanged = this.#updateLabel(level, false) || hasChanged;
        continue;
      }

      const placement = this.placementById.get(level.id);
      if (!placement) {
        hasChanged = this.#updateLabel(level, false) || hasChanged;
        continue;
      }

      // Must also consider anchor in update as it depends on camera.
      const [pos3D, anchor] = this.placer.tryToPlace(placement, level.id);
      if (!pos3D) {
        // means we couldn't fit it.
        hasChanged = this.#updateLabel(level, false) || hasChanged;
        continue;
      }

      hasChanged = this.#updateLabel(level, true, pos3D, anchor) || hasChanged;
    }

    for (const levelId of unseen) {
      const label = this.labelById.get(levelId);
      hasChanged = label.isVisible() || hasChanged;
      label.setVisible(false);
    }

    this.isDirty = false;
    return hasChanged;
  }

  #getFilteredLevels() {

    const levels = this.facility.facetsManager.getFacet(FacetTypes.levels);

    if (!levels) return [];

    const filtered = [];
    for (const level of levels) {
      if (level.isUnassignedId) continue;
      filtered.push(level);
    }

    // Sort by filtered levels so that we keep the "selected" labels with highest priority.
    return filtered.sort((l, r) => {

      if (l.selected && !r.selected) {
        return -1;
      } else if (r.selected && !l.selected) {
        return 1;
      }

      return r.elev - l.elev;
    });
  }

  #invalidateFilteredLevels() {
    this.filteredLevels = this.#getFilteredLevels();
    this.debouncedInvalidatePlacement();
  }

  #invalidatePlacement(useCacheOnly) {
    if (!this.filteredLevels || !this.isVisible()) return;

    if (this.filteredLevels.length === 0) {
      this.placementById.clear();
    } else {
      this.placementById = this.placer.guessPlacement(this.facility, this.filteredLevels, useCacheOnly);
    }

    this.invalidate();
  }

  #makeLabel(level) {
    const label = new LevelLabel(level, level.id, this.hud);

    const et = label.label;
    et.addEventListener(Label3D.Events.CLICK, ({ event }) => this.onSelect(event, label));
    et.addEventListener(Label3D.Events.ON_MOUSE_OVER, ({ event }) => this.onMouseOver(event, level));
    et.addEventListener(Label3D.Events.ON_MOUSE_OUT, ({ event }) => this.onMouseOut(event));
    et.addEventListener(Label3D.Events.DBL_CLICK, ({ event }) => this.onDblClick(event, level));

    this.labelById.set(level.id, label);

    //To update the faded style in case another level label is already selected
    label.setSelected(false, this.#hasSelected);

    return label;
  }

  #onGeomLoaded() {
    // On final frame is needed here as invalidate placement relies on a floor cached mesh which can change
    // as relevant geometries are loaded. This allows on camera move to instead used the cached version since this
    // geom loaded will take care of requesting a non-cached re-computation when needed.
    this.debouncedInvalidatePlacement();
  }

  #updateLabel(level, isVisible, pos3D, anchor) {
    let label = this.labelById.get(level.id);

    if (isVisible) {
      label ??= this.#makeLabel(level);
      label.updatePlacement(pos3D, anchor);
      label.setVisible(true);
      return true;
    }
    if (!label) return false;

    const wasVisible = label.isVisible();
    label.setVisible(false);
    return wasVisible;
  }
}

const _lvlBounds = new THREE.Box3();
const _bounds = new THREE.Box3();
export function getLevelBounds(facility, level) {

  const cachedMesh = facility.facetsManager.facetsEffects.getFloorMesh(level);
  if (cachedMesh?.geometry) {
    return cachedMesh.geometry.boundingBox.clone().applyMatrix4(cachedMesh.matrix);
  }

  // Slow code path, bounds from everything inside the level.
  _lvlBounds.makeEmpty();
  for (const [model, ids] of level.ids) {
    for (const dbId of ids) {
      model.getElementBounds(dbId, _bounds);
      _lvlBounds.union(_bounds);
    }
  }

  return _lvlBounds;
}